export type ResizableOrientation = 'horizontal' | 'vertical'
export type ResizableDirection = 'ltr' | 'rtl'

export interface ResizablePanelConstraint {
  readonly id: string
  readonly defaultSize?: number
  readonly minSize?: number
  readonly maxSize?: number
  readonly collapsible?: boolean
  readonly collapsedSize?: number
}

export type KeyboardResizeIntent
  = | { readonly kind: 'delta', readonly delta: number }
    | { readonly kind: 'minimum' | 'maximum' | 'toggle' }

export interface CollapsedPanelLayout {
  readonly layout: readonly number[]
  readonly restoreSize: number
}

export interface HandleResizeTransaction {
  readonly layout: readonly number[]
  readonly collapsedPanelId?: string
  readonly expandedPanelId?: string
  readonly restoreSize?: number
}

export interface PrimaryPanelRange {
  readonly min: number
  readonly max: number
  readonly now: number
}

const totalPercentage = 100
const precision = 1_000_000
const epsilon = 1 / precision

interface Bounds {
  min: number
  max: number
}

function isFinitePercentage(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= totalPercentage
}

function boundsFor(
  constraint: ResizablePanelConstraint,
  requestedSize?: number,
): Bounds | undefined {
  const min = constraint.minSize ?? 0
  const max = constraint.maxSize ?? totalPercentage
  const collapsedSize = constraint.collapsedSize ?? 0
  if (
    !constraint.id
    || !isFinitePercentage(min)
    || !isFinitePercentage(max)
    || min > max
    || !isFinitePercentage(collapsedSize)
    || (constraint.collapsible && collapsedSize > min)
    || (constraint.defaultSize !== undefined && !isFinitePercentage(constraint.defaultSize))
  ) {
    return undefined
  }

  if (
    constraint.collapsible
    && requestedSize !== undefined
    && Math.abs(requestedSize - collapsedSize) <= epsilon
  ) {
    return { min: collapsedSize, max: collapsedSize }
  }
  return { min, max }
}

function quantizeLayout(values: readonly number[], bounds: readonly Bounds[]): readonly number[] | undefined {
  const target = totalPercentage * precision
  const minimumUnits = bounds.map(({ min }) => Math.ceil((min - epsilon) * precision))
  const maximumUnits = bounds.map(({ max }) => Math.floor((max + epsilon) * precision))
  const rawUnits = values.map(value => value * precision)
  const units = rawUnits.map((value, index) => Math.max(
    minimumUnits[index],
    Math.min(maximumUnits[index], Math.floor(value + epsilon)),
  ))
  let remainder = target - units.reduce((sum, value) => sum + value, 0)

  const addOrder = rawUnits
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index)
  const removeOrder = [...addOrder].sort(
    (left, right) => left.fraction - right.fraction || right.index - left.index,
  )

  while (remainder !== 0) {
    const order = remainder > 0 ? addOrder : removeOrder
    let changed = false
    for (const { index } of order) {
      if (remainder > 0 && units[index] < maximumUnits[index]) {
        units[index]++
        remainder--
        changed = true
      }
      else if (remainder < 0 && units[index] > minimumUnits[index]) {
        units[index]--
        remainder++
        changed = true
      }
      if (remainder === 0) {
        break
      }
    }
    if (!changed) {
      return undefined
    }
  }

  const percentages = units.map(value => value / precision)
  const floatingTotal = percentages.reduce((sum, value) => sum + value, 0)
  if (floatingTotal !== totalPercentage) {
    percentages[percentages.length - 1] += totalPercentage - floatingTotal
  }
  return Object.freeze(percentages)
}

/**
 * Projects requested panel percentages onto their bounded 100% simplex.
 * The projection is deterministic, immutable, and proportional until a bound
 * removes a panel from the remaining allocation.
 */
export function normalizeResizableLayout(
  requested: readonly (number | undefined)[] | undefined,
  constraints: readonly ResizablePanelConstraint[],
): readonly number[] | undefined {
  const supplied = requested === undefined
    ? constraints.map(({ defaultSize }) => defaultSize)
    : requested.map((value, index) => value ?? constraints[index]?.defaultSize)
  const suppliedTotal = supplied.reduce<number>(
    (sum, value) => sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0),
    0,
  )
  const missingCount = supplied.filter(value => value === undefined).length
  const missingShare = missingCount > 0
    ? Math.max(0, totalPercentage - suppliedTotal) / missingCount
    : 0
  const seeds = supplied.map(value => value ?? missingShare)
  if (
    constraints.length === 0
    || (requested !== undefined && requested.length !== constraints.length)
    || seeds.some(value => !isFinitePercentage(value))
    || new Set(constraints.map(({ id }) => id)).size !== constraints.length
  ) {
    return undefined
  }

  const bounds = constraints.map((constraint, index) => boundsFor(constraint, seeds[index]))
  if (bounds.includes(undefined)) {
    return undefined
  }
  const validBounds = bounds as Bounds[]
  const minimumTotal = validBounds.reduce((sum, { min }) => sum + min, 0)
  const maximumTotal = validBounds.reduce((sum, { max }) => sum + max, 0)
  if (minimumTotal > totalPercentage + epsilon || maximumTotal < totalPercentage - epsilon) {
    return undefined
  }

  const result = Array.from({ length: seeds.length }, () => 0)
  const remaining = new Set(seeds.map((_, index) => index))
  let remainingTotal = totalPercentage

  while (remaining.size > 0) {
    const weightTotal = Array.from(remaining).reduce((sum, index) => sum + seeds[index], 0)
    const equalShare = remainingTotal / remaining.size
    let fixedAny = false

    for (const index of remaining) {
      const share = weightTotal > 0
        ? remainingTotal * seeds[index] / weightTotal
        : equalShare
      const { min, max } = validBounds[index]
      if (share < min - epsilon) {
        result[index] = min
        remainingTotal -= min
        remaining.delete(index)
        fixedAny = true
      }
      else if (share > max + epsilon) {
        result[index] = max
        remainingTotal -= max
        remaining.delete(index)
        fixedAny = true
      }
    }

    if (!fixedAny) {
      const finalWeightTotal = Array.from(remaining).reduce(
        (sum, index) => sum + seeds[index],
        0,
      )
      const finalEqualShare = remainingTotal / remaining.size
      for (const index of remaining) {
        result[index] = finalWeightTotal > 0
          ? remainingTotal * seeds[index] / finalWeightTotal
          : finalEqualShare
      }
      break
    }
  }

  return quantizeLayout(result, validBounds)
}

function capacities(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
): readonly Bounds[] | undefined {
  const result = constraints.map((constraint, index) => boundsFor(constraint, layout[index]))
  return result.includes(undefined) ? undefined : result as Bounds[]
}

function totalCapacity(
  indices: readonly number[],
  layout: readonly number[],
  bounds: readonly Bounds[],
  grow: boolean,
): number {
  return indices.reduce(
    (sum, index) => sum + (grow ? bounds[index].max - layout[index] : layout[index] - bounds[index].min),
    0,
  )
}

function applyClosestFirst(
  values: number[],
  indices: readonly number[],
  bounds: readonly Bounds[],
  amount: number,
  grow: boolean,
) {
  let remaining = amount
  for (const index of indices) {
    const capacity = grow ? bounds[index].max - values[index] : values[index] - bounds[index].min
    const applied = Math.min(remaining, Math.max(0, capacity))
    values[index] += grow ? applied : -applied
    remaining -= applied
    if (remaining <= epsilon) {
      return
    }
  }
}

/** Resize the boundary after `handleIndex`, distributing from the handle out. */
export function resizeAtHandle(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  handleIndex: number,
  delta: number,
): readonly number[] | undefined {
  const normalized = normalizeResizableLayout(layout, constraints)
  if (
    !normalized
    || !Number.isInteger(handleIndex)
    || handleIndex < 0
    || handleIndex >= normalized.length - 1
    || !Number.isFinite(delta)
  ) {
    return undefined
  }
  if (Math.abs(delta) <= epsilon) {
    return normalized
  }

  const bounds = capacities(normalized, constraints)
  if (!bounds) {
    return undefined
  }
  const left = Array.from({ length: handleIndex + 1 }, (_, offset) => handleIndex - offset)
  const right = Array.from(
    { length: normalized.length - handleIndex - 1 },
    (_, offset) => handleIndex + offset + 1,
  )
  const positive = delta > 0
  const grow = positive ? left : right
  const shrink = positive ? right : left
  const amount = Math.min(
    Math.abs(delta),
    totalCapacity(grow, normalized, bounds, true),
    totalCapacity(shrink, normalized, bounds, false),
  )
  const result = [...normalized]
  applyClosestFirst(result, grow, bounds, amount, true)
  applyClosestFirst(result, shrink, bounds, amount, false)
  return quantizeLayout(result, bounds)
}

function neighborOrder(length: number, panelIndex: number): number[] {
  const result: number[] = []
  for (let distance = 1; distance < length; distance++) {
    const after = panelIndex + distance
    const before = panelIndex - distance
    if (after < length) {
      result.push(after)
    }
    if (before >= 0) {
      result.push(before)
    }
  }
  return result
}

function setPanelSize(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  panelIndex: number,
  requestedSize: number,
  allowCollapsedTarget: boolean,
): readonly number[] | undefined {
  const normalized = normalizeResizableLayout(layout, constraints)
  const targetConstraint = constraints[panelIndex]
  if (!normalized || !targetConstraint || !isFinitePercentage(requestedSize)) {
    return undefined
  }
  const targetBounds = boundsFor(targetConstraint)
  if (!targetBounds) {
    return undefined
  }
  const collapsedSize = targetConstraint.collapsedSize ?? 0
  const target = allowCollapsedTarget
    ? collapsedSize
    : Math.max(targetBounds.min, Math.min(targetBounds.max, requestedSize))
  const availableBounds = capacities(normalized, constraints)
  if (!availableBounds) {
    return undefined
  }
  const bounds = [...availableBounds]
  bounds[panelIndex] = { min: target, max: target }
  const difference = target - normalized[panelIndex]
  const result = [...normalized]
  const neighbors = neighborOrder(result.length, panelIndex)
  const amount = Math.min(
    Math.abs(difference),
    totalCapacity(neighbors, normalized, bounds, difference < 0),
  )
  if (amount + epsilon < Math.abs(difference)) {
    return undefined
  }
  result[panelIndex] = target
  applyClosestFirst(result, neighbors, bounds, amount, difference < 0)
  return quantizeLayout(result, bounds)
}

interface AdjacentPanelResize {
  readonly layout: readonly number[]
  readonly reachedRequestedTarget: boolean
}

function resizeAdjacentPanelAtHandle(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  handleIndex: number,
  panelIndex: number,
  requestedSize: number,
): AdjacentPanelResize | undefined {
  const normalized = normalizeResizableLayout(layout, constraints)
  const constraint = constraints[panelIndex]
  if (
    !normalized
    || !Number.isInteger(handleIndex)
    || handleIndex < 0
    || handleIndex >= normalized.length - 1
    || (panelIndex !== handleIndex && panelIndex !== handleIndex + 1)
    || !constraint
    || !isFinitePercentage(requestedSize)
  ) {
    return undefined
  }

  const expandedBounds = boundsFor(constraint)
  const availableBounds = capacities(normalized, constraints)
  if (!expandedBounds || !availableBounds) {
    return undefined
  }

  const collapsedSize = constraint.collapsedSize ?? 0
  const requestsCollapsed = Boolean(
    constraint.collapsible
    && Math.abs(requestedSize - collapsedSize) <= epsilon,
  )
  const requestedTarget = requestsCollapsed
    ? collapsedSize
    : Math.max(expandedBounds.min, Math.min(expandedBounds.max, requestedSize))
  const current = normalized[panelIndex]
  const opposite = panelIndex === handleIndex
    ? Array.from(
        { length: normalized.length - handleIndex - 1 },
        (_, offset) => handleIndex + offset + 1,
      )
    : Array.from({ length: handleIndex + 1 }, (_, offset) => handleIndex - offset)

  let target = requestedTarget
  if (target < current - epsilon) {
    const capacity = totalCapacity(opposite, normalized, availableBounds, true)
    if (requestsCollapsed && capacity + epsilon < current - collapsedSize) {
      target = Math.max(expandedBounds.min, current - capacity)
    }
    else {
      target = Math.max(target, current - capacity)
    }
  }
  else if (target > current + epsilon) {
    const capacity = totalCapacity(opposite, normalized, availableBounds, false)
    if (
      Math.abs(current - collapsedSize) <= epsilon
      && target >= expandedBounds.min
      && capacity + epsilon < expandedBounds.min - collapsedSize
    ) {
      target = collapsedSize
    }
    else {
      target = Math.min(target, current + capacity)
    }
  }

  if (Math.abs(target - current) <= epsilon) {
    return Object.freeze({
      layout: normalized,
      reachedRequestedTarget: Math.abs(current - requestedTarget) <= epsilon,
    })
  }

  const difference = target - current
  const bounds = [...availableBounds]
  bounds[panelIndex] = { min: target, max: target }
  const result = [...normalized]
  result[panelIndex] = target
  applyClosestFirst(result, opposite, bounds, Math.abs(difference), difference < 0)
  const resized = quantizeLayout(result, bounds)
  return resized
    ? Object.freeze({
        layout: resized,
        reachedRequestedTarget: Math.abs(resized[panelIndex] - requestedTarget) <= epsilon,
      })
    : undefined
}

/**
 * Moves the primary panel before a handle toward a requested size without
 * borrowing capacity from panels on the same side of the separator.
 */
export function resizePrimaryPanelAtHandle(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  handleIndex: number,
  requestedSize: number,
): readonly number[] | undefined {
  return resizeAdjacentPanelAtHandle(
    layout,
    constraints,
    handleIndex,
    handleIndex,
    requestedSize,
  )?.layout
}

/** Feasible ARIA values for the primary panel before a handle. */
export function primaryPanelRangeAtHandle(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  handleIndex: number,
): PrimaryPanelRange | undefined {
  const normalized = normalizeResizableLayout(layout, constraints)
  const constraint = constraints[handleIndex]
  if (!normalized || !constraint || handleIndex >= normalized.length - 1) {
    return undefined
  }
  const minimumTarget = constraint.collapsible
    ? constraint.collapsedSize ?? 0
    : constraint.minSize ?? 0
  const minimum = resizePrimaryPanelAtHandle(
    normalized,
    constraints,
    handleIndex,
    minimumTarget,
  )
  const maximum = resizePrimaryPanelAtHandle(
    normalized,
    constraints,
    handleIndex,
    constraint.maxSize ?? totalPercentage,
  )
  if (!minimum || !maximum) {
    return undefined
  }
  return Object.freeze({
    min: minimum[handleIndex],
    max: maximum[handleIndex],
    now: normalized[handleIndex],
  })
}

/** Resize one programmatic panel and transfer its complete delta to nearest siblings. */
export function resizePanelLayout(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  panelIndex: number,
  requestedSize: number,
): readonly number[] | undefined {
  if (!Number.isInteger(panelIndex) || panelIndex < 0 || panelIndex >= constraints.length) {
    return undefined
  }
  return setPanelSize(layout, constraints, panelIndex, requestedSize, false)
}

export function collapsePanelLayout(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  panelIndex: number,
): CollapsedPanelLayout | undefined {
  const constraint = constraints[panelIndex]
  if (!constraint?.collapsible) {
    return undefined
  }
  const normalized = normalizeResizableLayout(layout, constraints)
  if (!normalized) {
    return undefined
  }
  const collapsedSize = constraint.collapsedSize ?? 0
  const alreadyCollapsed = Math.abs(normalized[panelIndex] - collapsedSize) <= epsilon
  const collapsed = setPanelSize(normalized, constraints, panelIndex, constraint.collapsedSize ?? 0, true)
  if (!collapsed) {
    return undefined
  }
  return Object.freeze({
    layout: collapsed,
    restoreSize: alreadyCollapsed
      ? constraint.defaultSize ?? constraint.minSize ?? collapsedSize
      : normalized[panelIndex],
  })
}

export function expandPanelLayout(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  panelIndex: number,
  restoreSize?: number,
): readonly number[] | undefined {
  const constraint = constraints[panelIndex]
  if (!constraint?.collapsible) {
    return undefined
  }
  const target = isFinitePercentage(restoreSize)
    ? restoreSize
    : constraint.defaultSize ?? constraint.minSize ?? 0
  return setPanelSize(layout, constraints, panelIndex, target, false)
}

/**
 * Pointer-oriented handle transaction with atomic midpoint collapse/restore.
 * Continuous movement still delegates to the closest-first bounded solver.
 */
export function resizeAtHandleWithCollapse(
  layout: readonly number[],
  constraints: readonly ResizablePanelConstraint[],
  handleIndex: number,
  delta: number,
  restoreSizes: Readonly<Record<string, number>> = {},
): HandleResizeTransaction | undefined {
  const normalized = normalizeResizableLayout(layout, constraints)
  if (
    !normalized
    || !Number.isInteger(handleIndex)
    || handleIndex < 0
    || handleIndex >= normalized.length - 1
    || !Number.isFinite(delta)
  ) {
    return undefined
  }

  const shrinkingPanelIndex = delta < 0 ? handleIndex : handleIndex + 1
  const growingPanelIndex = delta > 0 ? handleIndex : handleIndex + 1
  const shrinkingConstraint = constraints[shrinkingPanelIndex]
  const growingConstraint = constraints[growingPanelIndex]

  if (shrinkingConstraint?.collapsible) {
    const collapsedSize = shrinkingConstraint.collapsedSize ?? 0
    const midpoint = collapsedSize + ((shrinkingConstraint.minSize ?? 0) - collapsedSize) / 2
    const rawSize = normalized[shrinkingPanelIndex] - Math.abs(delta)
    if (normalized[shrinkingPanelIndex] > collapsedSize + epsilon && rawSize < midpoint) {
      const collapsed = resizeAdjacentPanelAtHandle(
        normalized,
        constraints,
        handleIndex,
        shrinkingPanelIndex,
        collapsedSize,
      )
      if (collapsed?.reachedRequestedTarget) {
        return Object.freeze({
          layout: collapsed.layout,
          collapsedPanelId: shrinkingConstraint.id,
          restoreSize: normalized[shrinkingPanelIndex],
        })
      }
    }
  }

  if (growingConstraint?.collapsible) {
    const collapsedSize = growingConstraint.collapsedSize ?? 0
    const midpoint = collapsedSize + ((growingConstraint.minSize ?? 0) - collapsedSize) / 2
    const rawSize = normalized[growingPanelIndex] + Math.abs(delta)
    if (
      Math.abs(normalized[growingPanelIndex] - collapsedSize) <= epsilon
      && rawSize > midpoint
    ) {
      const expandedTarget = isFinitePercentage(restoreSizes[growingConstraint.id])
        ? restoreSizes[growingConstraint.id]
        : growingConstraint.defaultSize ?? growingConstraint.minSize ?? collapsedSize
      const expanded = resizeAdjacentPanelAtHandle(
        normalized,
        constraints,
        handleIndex,
        growingPanelIndex,
        expandedTarget,
      )
      if (expanded && expanded.layout[growingPanelIndex] >= (growingConstraint.minSize ?? 0) - epsilon) {
        return Object.freeze({
          layout: expanded.layout,
          expandedPanelId: growingConstraint.id,
        })
      }
    }
  }

  const resized = resizeAtHandle(normalized, constraints, handleIndex, delta)
  return resized ? Object.freeze({ layout: resized }) : undefined
}

export function pointerDeltaToPercent(
  startCoordinate: number,
  currentCoordinate: number,
  usableAxisSize: number,
  orientation: ResizableOrientation,
  direction: ResizableDirection,
): number | undefined {
  if (
    !Number.isFinite(startCoordinate)
    || !Number.isFinite(currentCoordinate)
    || !Number.isFinite(usableAxisSize)
    || usableAxisSize <= 0
  ) {
    return undefined
  }
  const physicalDelta = currentCoordinate - startCoordinate
  const logicalDelta = orientation === 'horizontal' && direction === 'rtl'
    ? -physicalDelta
    : physicalDelta
  return logicalDelta / usableAxisSize * totalPercentage
}

export function keyboardResizeIntent(
  key: string,
  shiftKey: boolean,
  orientation: ResizableOrientation,
  direction: ResizableDirection,
  step: number,
  largeStep: number,
): KeyboardResizeIntent | undefined {
  if (!Number.isFinite(step) || step <= 0 || !Number.isFinite(largeStep) || largeStep <= 0) {
    return undefined
  }
  if (key === 'Home') {
    return { kind: 'minimum' }
  }
  if (key === 'End') {
    return { kind: 'maximum' }
  }
  if (key === 'Enter') {
    return { kind: 'toggle' }
  }

  const amount = shiftKey ? largeStep : step
  if (orientation === 'vertical') {
    if (key === 'ArrowUp') {
      return { kind: 'delta', delta: -amount }
    }
    if (key === 'ArrowDown') {
      return { kind: 'delta', delta: amount }
    }
    return undefined
  }
  if (key === 'ArrowLeft') {
    return { kind: 'delta', delta: direction === 'rtl' ? amount : -amount }
  }
  if (key === 'ArrowRight') {
    return { kind: 'delta', delta: direction === 'rtl' ? -amount : amount }
  }
  return undefined
}
